import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { BudgetListItem, SettlementListItem } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { IsoDatePicker } from '../IsoDatePicker';
import { PagePrimaryFab } from '../ui/PagePrimaryFab';
import { budgetApi, settlementApi } from '../../lib/api';
import {
  getFirstDayOfPreviousMonthYmdInTimezone,
  resolveDisplayTimeZone,
} from '../../lib/userTodayYmd';
import { MoneyGivenVsExpensesChart } from './employer/MoneyGivenVsExpensesChart';
import { EmployerPendingReviewCard } from './employer/EmployerPendingReviewCard';
import { MoneyGivenCombinedCard } from './employer/MoneyGivenCombinedCard';
import {
  buildGivenVsExpensesBars,
  resolveEmployerMoneyGivenCardView,
} from '../../utils/employerMoneyGivenSettlement';

const isIsoDate = (value: string | null): value is string =>
  value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value);

const EMPLOYER_FINANCE_OPEN_RANGE_END_YMD = '9999-12-31';

export const EmployerMoneyGivenSettlementSection = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const timezone = resolveDisplayTimeZone(user?.settings.timezone);

  const [budgetStartDate, setBudgetStartDate] = useState(() => searchParams.get('budgetStart') ?? '');
  const [budgetEndDate, setBudgetEndDate] = useState(() => searchParams.get('budgetEnd') ?? '');
  const [budgets, setBudgets] = useState<BudgetListItem[]>([]);
  const [chartBudgets, setChartBudgets] = useState<BudgetListItem[]>([]);
  const [settlements, setSettlements] = useState<SettlementListItem[]>([]);
  const [budgetsLoading, setBudgetsLoading] = useState(true);
  const [chartBudgetsLoading, setChartBudgetsLoading] = useState(true);
  const [budgetLoadError, setBudgetLoadError] = useState<string | null>(null);
  const [budgetFilterError, setBudgetFilterError] = useState<string | null>(null);
  const [settlementsLoading, setSettlementsLoading] = useState(true);

  const fromPath = `${location.pathname}${location.search}`;

  const settlementPendingCountUnitLabel = (n: number) =>
    t(n === 1 ? 'task.list.summaryCountUnitOne' : 'task.list.summaryCountUnitOther');

  const pendingSettlements = useMemo(
    () =>
      settlements.filter(
        (row) => row.status === 'submitted' && row.isFromPreviousHelper !== true,
      ),
    [settlements]
  );

  const chartBars = useMemo(
    () => buildGivenVsExpensesBars(chartBudgets, settlements),
    [chartBudgets, settlements]
  );

  const sortedBudgets = useMemo(
    () =>
      [...budgets].sort((a, b) => {
        if (a.startDate !== b.startDate) {
          return b.startDate.localeCompare(a.startDate);
        }
        return b.endDate.localeCompare(a.endDate);
      }),
    [budgets]
  );

  const loadSettlements = useCallback(async () => {
    setSettlementsLoading(true);
    try {
      const pageSize = 100;
      const merged: SettlementListItem[] = [];
      for (let page = 1; page <= 20; page += 1) {
        const res = await settlementApi.list('all', page, pageSize);
        if (!res.success || !res.data) {
          break;
        }
        const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        merged.push(...items);
        if (items.length < pageSize) {
          break;
        }
      }
      setSettlements(merged);
    } finally {
      setSettlementsLoading(false);
    }
  }, []);

  const loadChartBudgets = useCallback(async () => {
    setChartBudgetsLoading(true);
    try {
      const res = await budgetApi.list('all', 1, 100, '2000-01-01', EMPLOYER_FINANCE_OPEN_RANGE_END_YMD);
      if (!res.success || !res.data) {
        setChartBudgets([]);
        return;
      }
      const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
      setChartBudgets(items);
    } finally {
      setChartBudgetsLoading(false);
    }
  }, []);

  useEffect(() => {
    const defaultStart = getFirstDayOfPreviousMonthYmdInTimezone(timezone);
    if (!isIsoDate(searchParams.get('budgetStart'))) {
      setBudgetStartDate(defaultStart);
    }
    if (!isIsoDate(searchParams.get('budgetEnd'))) {
      setBudgetEndDate('');
    }
  }, [searchParams, timezone]);

  useEffect(() => {
    const subTab = searchParams.get('subTab');
    if (subTab === 'receipts') {
      navigate('/receipts', { replace: true });
      return;
    }
    const tab = searchParams.get('tab');
    if (tab === 'budget' || tab === 'settlement') {
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      next.delete('subTab');
      next.delete('recordStart');
      next.delete('recordEnd');
      next.delete('recordStatus');
      next.delete('receiptStart');
      next.delete('receiptEnd');
      next.delete('receiptStatus');
      setSearchParams(next, { replace: true });
    }
  }, [navigate, searchParams, setSearchParams]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (budgetStartDate !== '') {
      nextParams.set('budgetStart', budgetStartDate);
    } else {
      nextParams.delete('budgetStart');
    }
    if (budgetEndDate !== '') {
      nextParams.set('budgetEnd', budgetEndDate);
    } else {
      nextParams.delete('budgetEnd');
    }
    nextParams.delete('tab');
    nextParams.delete('subTab');
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [budgetEndDate, budgetStartDate, searchParams, setSearchParams]);

  useEffect(() => {
    void loadSettlements();
    void loadChartBudgets();
  }, [loadChartBudgets, loadSettlements]);

  useEffect(() => {
    const run = async () => {
      setBudgetsLoading(true);
      setBudgetLoadError(null);
      setBudgetFilterError(null);
      if (!budgetStartDate) {
        setBudgets([]);
        setBudgetsLoading(false);
        return;
      }
      const effectiveEnd = budgetEndDate || EMPLOYER_FINANCE_OPEN_RANGE_END_YMD;
      if (budgetStartDate > effectiveEnd) {
        setBudgets([]);
        setBudgetFilterError(t('budget.validation.dates'));
        setBudgetsLoading(false);
        return;
      }
      try {
        const res = await budgetApi.list('all', 1, 100, budgetStartDate, effectiveEnd);
        if (res.success && res.data) {
          const items = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
          setBudgets(items);
          return;
        }
        setBudgetLoadError(res.error ?? t('budget.loadError'));
      } catch {
        setBudgetLoadError(t('budget.loadError'));
      } finally {
        setBudgetsLoading(false);
      }
    };
    void run();
  }, [budgetEndDate, budgetStartDate, t]);

  const handleReviewNow = () => {
    if (pendingSettlements.length === 1) {
      navigate(`/settlements/${pendingSettlements[0].id}`, { state: { from: fromPath } });
      return;
    }
    navigate('/settlements/pending-review');
  };

  const handlePendingCardNavigate = () => {
    navigate('/settlements/pending-review');
  };

  const handleNavigateDetail = (path: string) => {
    navigate(path, { state: { from: fromPath } });
  };

  return (
    <section
      aria-label={t('settlement.employerFinanceTabsAria')}
      className="flex min-w-0 flex-col gap-6 overflow-x-hidden"
    >
      <MoneyGivenVsExpensesChart
        bars={chartBars}
        loading={chartBudgetsLoading || settlementsLoading}
        emptyLabel={t('budget.empty')}
        title={t('settlement.givenVsExpensesTitle')}
        subtitle={t('settlement.givenVsExpensesSubtitle')}
        givenLabel={t('settlement.givenVsExpensesGiven')}
        expensesLabel={t('settlement.givenVsExpensesSpent')}
        pendingExpensesLabel={t('settlement.givenVsExpensesPendingSpent')}
      />

      <EmployerPendingReviewCard
        count={pendingSettlements.length}
        title={t('settlement.pendingReviewPageTitle')}
        countUnitLabel={settlementPendingCountUnitLabel(pendingSettlements.length)}
        reviewNowLabel={t('settlement.reviewNow')}
        onReviewNow={pendingSettlements.length === 1 ? handleReviewNow : handlePendingCardNavigate}
      />

      <div className="flex items-center gap-3">
        <IsoDatePicker
          id="employer-money-given-start"
          ariaLabel={t('budget.filterStartDate')}
          value={budgetStartDate}
          onChange={setBudgetStartDate}
          maxDate={budgetEndDate || undefined}
          className="min-w-0 flex-1"
          valueFormat="iso"
        />
        <span className="shrink-0 text-sm font-bold text-gray-500 dark:text-gray-400" aria-hidden>
          {t('settlement.helperConfirmDateRangeSeparator')}
        </span>
        <IsoDatePicker
          id="employer-money-given-end"
          ariaLabel={t('settlement.employerFilterToDateOptional')}
          value={budgetEndDate}
          onChange={setBudgetEndDate}
          minDate={budgetStartDate || undefined}
          className="min-w-0 flex-1"
          valueFormat="iso"
        />
      </div>

      {budgetFilterError ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {budgetFilterError}
        </p>
      ) : null}

      {budgetsLoading || settlementsLoading ? (
        <div className="flex justify-center py-12" role="status" aria-busy="true">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
        </div>
      ) : budgetLoadError ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {budgetLoadError}
        </p>
      ) : sortedBudgets.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('budget.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-4" role="list">
          {sortedBudgets.map((row) => {
            const cardView = resolveEmployerMoneyGivenCardView(row, settlements);
            return (
              <li key={row.id}>
                <MoneyGivenCombinedCard
                  row={row}
                  cardView={cardView}
                  settlements={settlements}
                  fromPath={fromPath}
                  periodLabel={t('settlement.period', { start: row.startDate, end: row.endDate })}
                  formatCreatedAtLabel={(createdAt) =>
                    t('budget.timeLabel', { value: createdAt ?? '—' })
                  }
                  includesAddOnLabel={t('budget.mergedChildCount', {
                    count: row.mergedChildCount ?? 0,
                  })}
                  moneyGivenAmountLabel={t('settlement.budgetAmount')}
                  settlementResultLabel={t('settlement.balance')}
                  mainAmountLabel={t('budget.mainAmountLabel')}
                  subAmountLabel={t('budget.subAmountLabel')}
                  addOnLabel={t('budget.addOn')}
                  editAriaLabel={t('budget.editAria', { start: row.startDate, end: row.endDate })}
                  expandAriaLabel={t('budget.expandChildrenAria', {
                    start: row.startDate,
                    end: row.endDate,
                  })}
                  collapseAriaLabel={t('budget.collapseChildrenAria', {
                    start: row.startDate,
                    end: row.endDate,
                  })}
                  badgeUnsettledLabel={t('settlement.status.unsettled')}
                  badgePendingReviewLabel={t('settlement.status.submitted')}
                  badgeSettledLabel={t('settlement.status.settled')}
                  onNavigateDetail={handleNavigateDetail}
                  readOnly={row.isFromPreviousHelper === true}
                />
              </li>
            );
          })}
        </ul>
      )}

      <PagePrimaryFab to="/budgets/new" ariaLabel={t('budget.addNewAria')}>
        <span aria-hidden className="text-lg leading-none">
          +
        </span>
        {t('budget.addNew')}
      </PagePrimaryFab>
    </section>
  );
};
